/**
 * UnderstandingCard — calm "understanding before routing" surface.
 *
 * Operator brief (Feb 28, 2026):
 *   "Understanding response BEFORE guidance/routing. The system
 *    must FIRST respond with understanding before asking
 *    questions or navigating anywhere.
 *
 *    Core doctrine:
 *      • understanding before routing
 *      • acknowledgement before workflow
 *      • guidance before navigation
 *      • adaptive support instead of categorisation
 *      • no silent teleportation between systems
 *
 *    Goal emotional outcome:
 *      'This system understands me and is guiding me calmly.'
 *      Not: 'I typed something and got bounced between systems.'"
 *
 * Progressive-disclosure stages (ALL in-modal, NO navigation):
 *
 *   Stage 1 · Acknowledge
 *     "That's completely okay. A lot of learners feel nervous
 *      about roundabouts at first because there's a lot
 *      happening at once."
 *
 *   Stage 2 · Bridge
 *     "We can break this down slowly and explain it in a way
 *      that suits how you learn best."
 *
 *   Stage 3 · Invite (the question, not the answer)
 *     "Would you like me to help figure out the best way for
 *      you to learn?"
 *     Four calm mode-choice buttons:
 *       (a) Yes, help me choose            → reveal multi-select
 *       (b) Just explain it simply first   → reveal plain words
 *       (c) Show me visual examples        → reveal analogy
 *       (d) Skip for now                   → close modal
 *
 *   Stage 4 · Multi-select preferences (only after Yes-help-me-choose)
 *     8 toggle chips. The learner picks any combination, no
 *     forced single choice. POSTs to /api/learning/my-preferences
 *     (additive backend field — Lesson.js still reads the legacy
 *     `preferred_style` mirrored from index 0).
 *
 *   Stage 5 · Adaptive follow-up
 *     A calm one-liner reflecting their pick(s) without
 *     teleporting them anywhere. The learner closes the modal
 *     themselves when they're ready.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TOPIC_LIBRARY = {
  roundabouts: {
    label: "Roundabouts",
    headingKicker: "Worried about roundabouts?",
    acknowledgement:
      "That's completely okay. A lot of learners feel nervous about roundabouts at first because there's a lot happening at once.",
    analogy: {
      title: "Think of a roundabout like a playground merry-go-round.",
      body: [
        "When a fast merry-go-round is full of people, it feels chaotic. There's no obvious way on, no obvious gap.",
        "But as people get off, gaps appear — small, natural openings between the bodies still on the ride. You don't fight your way on. You wait for one of those gaps, step in calmly, and you're moving with the rest.",
        "A roundabout works the same way. Cars leaving, slowing, reacting to each other — they create natural gaps for you. You're not fighting traffic. You're joining the flow.",
      ],
      reveal: "One of those vehicles is you.",
    },
    simpleWords:
      "A roundabout is just a place where cars take turns. You wait for a safe gap, then move into it. You're not racing anyone — you're joining a flow that's already happening.",
  },
};

const GENERIC = {
  acknowledgement:
    "Thank you for telling me that. That kind of worry is completely normal — and it's good that you noticed it.",
  bridge:
    "We can break this down slowly and explain it in a way that suits how you learn best.",
  simpleWords:
    "When something feels stressful in driving, it usually means the situation had more going on than you'd had time to read. The fix isn't more pressure. It's slowing down, noticing one part at a time, and giving yourself space to think.",
};

// Eight learning-preference chips. The first 7 ids map 1-1 to the
// backend `learning_hub.LEARNING_STYLES` catalogue so a save round-
// trips cleanly through `POST /api/learning/my-preferences`. The
// 8th ("quick_summaries") is operator-named in the brief but not
// yet in the backend catalogue; we surface it locally and ignore
// it on save so the UI never silently drops the user's intent —
// the toast still reports what stuck. Future backend extension
// can wire it in additively without touching this file.
const PREFERENCE_CHIPS = [
  { id: "visual",       label: "Visual examples" },
  { id: "reading",      label: "Written explanations" },
  { id: "step_by_step", label: "Step-by-step breakdowns" },
  { id: "analogies",    label: "Real-world analogies" },
  { id: "audio",        label: "Audio / spoken explanation" },
  { id: "practical",    label: "Practical examples" },
  { id: "quick",        label: "Quick summaries first" },
  { id: "detail",       label: "Detailed understanding" },
];

const BRIDGE_SENTENCE =
  "We can break this down slowly and explain it in a way that suits how you learn best.";
const INVITE_QUESTION =
  "Would you like me to help figure out the best way for you to learn?";

// Build the calm "we remember you" sentence shown the moment the
// card re-opens for a learner who has already saved preferences.
// Doctrine (Feb 28, 2026 — operator brief):
//   • Use the actual saved chip labels.
//   • Keep it calm and optional. "I'll stick with that — or you
//     can change it."
//   • Never imply the learner is fixed as one type.
//   • If no saved prefs → return null (the JSX guards and hides
//     the whole sentence so nothing extra renders).
function memorySentence(savedIds) {
  const labelById = Object.fromEntries(
    PREFERENCE_CHIPS.map((c) => [c.id, c.label.toLowerCase()]),
  );
  const labels = savedIds.map((id) => labelById[id]).filter(Boolean);
  if (labels.length === 0) return null;
  let joined;
  if (labels.length === 1) {
    joined = labels[0];
  } else if (labels.length === 2) {
    joined = `${labels[0]} and ${labels[1]}`;
  } else {
    joined =
      `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
  }
  return `Last time you picked ${joined}. I'll stick with that — or you can change it.`;
}

// Build the calm reflective sentence the card shows immediately
// after the learner saves their picks. The brief is explicit
// about the four shapes, and the labels are taken from the chip
// strings the learner actually saw — lowercased for natural
// mid-sentence flow.
//
//   1 selected   → "I'll keep this simple and use {a} to help you understand."
//   2 selected   → "I'll explain things with {a} and {b} from now on."
//   3+ selected  → "I'll use a mix of {a}, {b}, and other approaches that suit how you learn."
//   0 / cleared  → "No problem — we can keep things simple and adjust later."
//
// Doctrine: NO "you are a {style} learner" categorisation. The
// sentence describes what the SYSTEM will do, never what the
// learner IS.
function reflectiveSentence(picks) {
  const labelById = Object.fromEntries(
    PREFERENCE_CHIPS.map((c) => [c.id, c.label.toLowerCase()]),
  );
  const labels = picks.map((id) => labelById[id]).filter(Boolean);
  if (labels.length === 0) {
    return "No problem — we can keep things simple and adjust later.";
  }
  if (labels.length === 1) {
    return `I'll keep this simple and use ${labels[0]} to help you understand.`;
  }
  if (labels.length === 2) {
    return `I'll explain things with ${labels[0]} and ${labels[1]} from now on.`;
  }
  return `I'll use a mix of ${labels[0]}, ${labels[1]}, and other approaches that suit how you learn.`;
}

export default function UnderstandingCard({ open, topic, phrase, onClose, onShowCurrentFocus }) {
  // Stage-machine state. Reveals are additive — once a section
  // opens it stays open, so the learner sees the full calm trail
  // of what they asked for rather than a flashing single panel.
  const [showSimple, setShowSimple] = useState(false);
  const [showAnalogy, setShowAnalogy] = useState(false);
  const [showPrefsPanel, setShowPrefsPanel] = useState(false);
  const [picks, setPicks] = useState([]);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  // Return-to-progression bridge (Feb 28, 2026 micro-batch).
  // The bridge appears AFTER the learner has finished at least
  // one part of the understanding conversation (saved prefs,
  // revealed simple explanation, or revealed visual examples).
  // It is the calm "shall we reconnect you with where you are
  // in your learning?" off-ramp. `bridgeDismissed` lets the
  // learner choose "I still want help with this" to hide the
  // bridge and keep exploring the modal.
  const [bridgeDismissed, setBridgeDismissed] = useState(false);
  // Reopen-memory state. Hydrated from /api/learning/my-preferences
  // on every modal mount/open. Used by the calm inline memory
  // sentence above the invite question, AND to pre-populate the
  // multi-select panel so "or you can change it" feels honest.
  // Fetch failure is silent — `savedPrefIds` stays empty, no
  // memory sentence renders, modal continues normally.
  const [savedPrefIds, setSavedPrefIds] = useState([]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/api/learning/my-preferences`, { withCredentials: true })
      .then(({ data }) => {
        if (cancelled) return;
        const ids = Array.isArray(data?.style_ids) ? data.style_ids : [];
        setSavedPrefIds(ids);
        // Pre-populate so the chip panel reflects current truth
        // when the learner taps "Yes, help me choose". Honest to
        // the wording: "I'll stick with that — or you can change it."
        setPicks(ids);
      })
      .catch(() => {
        // Silent — doctrine: "If the fetch fails, fail silently
        // and keep the modal working."
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const known = topic && TOPIC_LIBRARY[topic];
  const heading = known
    ? known.headingKicker
    : "Thanks for sharing that with me.";

  const togglePick = (id) => {
    setPicks((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
    setSaved(false);
  };

  const savePicks = async () => {
    setSaving(true);
    try {
      // Only send ids the backend catalogue actually knows about
      // (visual / reading / step_by_step / audio / practical /
      // analogies / repetition / etc.). The local-only chips
      // (`quick`, `detail`) are still remembered visually for
      // the rest of this session via `picks` state.
      const knownIds = new Set([
        "visual", "reading", "step_by_step",
        "audio", "practical", "analogies", "repetition",
      ]);
      const sendable = picks.filter((p) => knownIds.has(p));
      await axios.post(
        `${API}/api/learning/my-preferences`,
        { style_ids: sendable },
        { withCredentials: true },
      );
      setSaved(true);
      // Quiet confirmation only — the rich reflective sentence
      // renders inline in the card so the learner reads what
      // adapted, not just "Saved".
      toast.success("Saved.");
    } catch {
      toast.error("Couldn't save just now — your picks are remembered for this session.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="understanding-card-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="understanding-card-heading"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm px-4 sm:px-6 py-6 overflow-y-auto"
    >
      <div
        data-testid="understanding-card"
        data-topic={topic || "generic"}
        className="
          relative w-full max-w-xl bg-zinc-950 border border-amber-500/30
          rounded-3xl text-white shadow-[0_0_60px_-10px_rgba(251,191,36,0.25)]
          overflow-hidden
        "
      >
        <button
          type="button"
          data-testid="understanding-card-close"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 sm:p-8 space-y-5">
          {/* Stage 1 · Heading + acknowledgement (always shown) */}
          <header>
            <p className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-amber-400">
              Understanding before teaching
            </p>
            <h2
              id="understanding-card-heading"
              data-testid="understanding-card-heading"
              className="mt-2 text-2xl sm:text-3xl font-extrabold leading-tight tracking-tight"
            >
              {heading}
            </h2>
            {phrase && (
              <p
                data-testid="understanding-card-phrase"
                className="mt-2 text-[11px] text-zinc-500"
              >
                You wrote: <span className="text-zinc-400 italic">"{phrase.trim()}…"</span>
              </p>
            )}
          </header>

          <p
            data-testid="understanding-card-acknowledgement"
            className="text-sm sm:text-base text-zinc-200 leading-relaxed"
          >
            {known ? known.acknowledgement : GENERIC.acknowledgement}
          </p>

          {/* Stage 2 · Bridge (always shown — sits between
              acknowledgement and the calm question). */}
          <p
            data-testid="understanding-card-bridge"
            className="text-sm sm:text-base text-zinc-300 leading-relaxed"
          >
            {BRIDGE_SENTENCE}
          </p>

          {/* Stage 2b · Reopen memory — only renders if the
              learner has saved preferences. Calm, optional,
              describes the SYSTEM ("I'll stick with that"),
              never the learner ("you are a..."). Hidden
              entirely on first visit so nothing extra renders.
              Doctrine (Feb 28, 2026 reopen-memory micro-batch). */}
          {memorySentence(savedPrefIds) && (
            <p
              data-testid="understanding-card-memory-sentence"
              className="text-sm sm:text-base text-amber-200/90 italic leading-relaxed"
            >
              {memorySentence(savedPrefIds)}
            </p>
          )}

          {/* Stage 3 · Invite + 4 mode-choice buttons.
              Doctrine pin: NONE of these buttons navigates away.
              All reveal in-place or close the modal silently. */}
          <div className="pt-2 border-t border-zinc-800">
            <p
              data-testid="understanding-card-invite-question"
              className="text-sm sm:text-base text-white font-extrabold leading-snug"
            >
              {INVITE_QUESTION}
            </p>

            {/* FOS-10c · Failure 1 restoration. Two operator-named
                calm actions surfaced above the mode grid so the
                learner has one obvious way to stay in the
                canonical Understanding experience without
                dropping into any catalogue. Both trigger the
                existing in-place progressive disclosures — no
                navigation, no second learner pathway. */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="understanding-card-more-btn"
                onClick={() => { setShowSimple(true); setShowAnalogy(true); }}
                className="
                  h-11 px-4 rounded-2xl
                  bg-amber-400 hover:bg-amber-300 text-black text-sm font-extrabold
                  shadow-[0_0_20px_-6px_rgba(251,191,36,0.55)]
                  transition-colors text-left
                "
              >
                Help me understand this
              </button>
              <button
                type="button"
                data-testid="understanding-card-think-btn"
                onClick={() => setShowPrefsPanel(true)}
                className="
                  h-11 px-4 rounded-2xl
                  border border-amber-400/40 bg-zinc-900/60 text-white text-sm font-extrabold
                  hover:border-amber-300 transition-colors text-left
                "
              >
                Help me think this through
              </button>
            </div>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="understanding-card-mode-yes-help"
                onClick={() => setShowPrefsPanel(true)}
                className="
                  h-11 px-4 rounded-2xl
                  bg-amber-400 hover:bg-amber-300 text-black text-sm font-extrabold
                  shadow-[0_0_20px_-6px_rgba(251,191,36,0.55)]
                  transition-colors text-left
                "
              >
                Yes, help me choose
              </button>
              <button
                type="button"
                data-testid="understanding-card-mode-simply"
                onClick={() => setShowSimple(true)}
                className="
                  h-11 px-4 rounded-2xl
                  border border-zinc-700 bg-zinc-900/60 text-white text-sm font-extrabold
                  hover:border-zinc-500 transition-colors text-left
                "
              >
                Just explain it simply first
              </button>
              <button
                type="button"
                data-testid="understanding-card-mode-visual"
                onClick={() => setShowAnalogy(true)}
                className="
                  h-11 px-4 rounded-2xl
                  border border-zinc-700 bg-zinc-900/60 text-white text-sm font-extrabold
                  hover:border-zinc-500 transition-colors text-left
                "
              >
                Show me visual examples
              </button>
              <button
                type="button"
                data-testid="understanding-card-mode-skip"
                onClick={onClose}
                className="
                  h-11 px-4 rounded-2xl
                  border border-zinc-800 bg-transparent text-zinc-400 text-sm font-bold
                  hover:text-white hover:border-zinc-600 transition-colors text-left
                "
              >
                Skip for now
              </button>
            </div>
          </div>

          {/* Stage 4a · "Just explain it simply first" reveal */}
          {showSimple && (
            <section
              data-testid="understanding-card-simple-words"
              className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4 sm:p-5 text-sm text-zinc-200 leading-relaxed"
            >
              {known ? known.simpleWords : GENERIC.simpleWords}
            </section>
          )}

          {/* Stage 4b · "Show me visual examples" reveal — the
              operator-named merry-go-round analogy. */}
          {showAnalogy && known && (
            <section
              data-testid="understanding-card-analogy"
              className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4 sm:p-5"
            >
              <p className="text-[11px] uppercase tracking-wider font-extrabold text-amber-300">
                A way to picture it
              </p>
              <p className="mt-2 text-base sm:text-lg font-extrabold text-white leading-snug">
                {known.analogy.title}
              </p>
              <div className="mt-3 space-y-2 text-sm text-zinc-300 leading-relaxed">
                {known.analogy.body.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
              <p
                data-testid="understanding-card-reveal"
                className="mt-4 text-base font-extrabold text-amber-300"
              >
                {known.analogy.reveal}
              </p>
            </section>
          )}
          {showAnalogy && !known && (
            <section
              data-testid="understanding-card-analogy-unavailable"
              className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4 sm:p-5 text-sm text-zinc-300 leading-relaxed"
            >
              We don't have a visual example for this exact topic
              yet — when you're ready, an instructor can walk you
              through it without any pressure.
            </section>
          )}

          {/* Stage 4c · "Yes, help me choose" — multi-select panel */}
          {showPrefsPanel && (
            <section
              data-testid="understanding-card-prefs-panel"
              className="rounded-2xl border border-amber-500/30 bg-zinc-900/80 p-4 sm:p-5 space-y-4"
            >
              <div>
                <p className="text-[11px] uppercase tracking-wider font-extrabold text-amber-300">
                  How you learn best
                </p>
                <p className="mt-2 text-sm text-zinc-200 leading-relaxed">
                  Pick any combination that suits you — you can pick
                  more than one, and you can change this whenever you like.
                </p>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="understanding-card-prefs-options">
                {PREFERENCE_CHIPS.map((chip) => {
                  const active = picks.includes(chip.id);
                  return (
                    <button
                      type="button"
                      key={chip.id}
                      role="checkbox"
                      aria-checked={active}
                      data-testid={`pref-chip-${chip.id}`}
                      onClick={() => togglePick(chip.id)}
                      className={`
                        inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-bold transition-colors
                        ${active
                          ? "bg-amber-400 text-black border border-amber-300"
                          : "bg-zinc-950 text-zinc-200 border border-zinc-700 hover:border-zinc-500"
                        }
                      `}
                    >
                      {active && <Check className="w-3.5 h-3.5" />}
                      {chip.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <button
                  type="button"
                  data-testid="understanding-card-prefs-save"
                  disabled={saving}
                  onClick={savePicks}
                  className="
                    h-11 px-5 rounded-2xl
                    bg-amber-400 hover:bg-amber-300 text-black text-sm font-extrabold
                    disabled:bg-zinc-800 disabled:text-zinc-500
                    transition-colors
                  "
                >
                  {saving ? "Saving…" : saved ? "Saved" : "Save how I like to learn"}
                </button>
                {saved && (
                  <p
                    data-testid="understanding-card-prefs-saved-note"
                    className="text-sm text-zinc-200 leading-relaxed self-center"
                  >
                    {reflectiveSentence(picks)}
                  </p>
                )}
              </div>
            </section>
          )}

          {/* Stage 5 · Return-to-progression bridge.
              Doctrine (Feb 28, 2026): "This is not an ending. It
              is a return-to-progression bridge." The bridge
              appears AFTER the learner has finished any
              substantive part of the conversation (saved prefs,
              revealed simple words, revealed visual analogy). It
              never auto-navigates — the learner chooses. Tap "I
              still want help with this" to hide the bridge and
              keep exploring the modal. */}
          {(saved || showSimple || showAnalogy) && !bridgeDismissed && (
            <section
              data-testid="understanding-card-return-bridge"
              className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 sm:p-5 space-y-4"
            >
              <p
                data-testid="understanding-card-return-bridge-question"
                className="text-sm sm:text-base text-zinc-200 leading-relaxed"
              >
                Are you ready to get back to your current level and see if there's anything else you'd like to understand before moving on?
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="understanding-card-return-back-to-level"
                  onClick={onClose}
                  className="h-11 px-4 rounded-2xl bg-amber-400 hover:bg-amber-300 text-black text-sm font-extrabold text-left transition-colors"
                >
                  Back to my current level
                </button>
                <button
                  type="button"
                  data-testid="understanding-card-return-show-focus"
                  onClick={() => {
                    // The parent decides what "show me what I'm
                    // working on" means on the current surface
                    // (scroll, focus, etc.). The card never
                    // navigates on its own.
                    if (typeof onShowCurrentFocus === "function") {
                      onShowCurrentFocus();
                    }
                    onClose();
                  }}
                  className="h-11 px-4 rounded-2xl border border-zinc-700 bg-zinc-950 text-white text-sm font-extrabold text-left hover:border-zinc-500 transition-colors"
                >
                  Show me what I'm working on
                </button>
                <button
                  type="button"
                  data-testid="understanding-card-return-still-want-help"
                  onClick={() => setBridgeDismissed(true)}
                  className="h-11 px-4 rounded-2xl border border-zinc-700 bg-zinc-950 text-white text-sm font-extrabold text-left hover:border-zinc-500 transition-colors"
                >
                  I still want help with this
                </button>
                <button
                  type="button"
                  data-testid="understanding-card-return-done-for-now"
                  onClick={onClose}
                  className="h-11 px-4 rounded-2xl border border-zinc-800 bg-transparent text-zinc-400 text-sm font-bold text-left hover:text-white hover:border-zinc-600 transition-colors"
                >
                  Done for now
                </button>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
